/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import { apiErr } from "@/lib/utils";
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Save, User, Phone, MapPin, Clock, RefreshCw, Edit2, X, Lock } from "lucide-react";
import { VVF_REGIONI, getComandi, getSedi } from "../data/vvfData";
import { fmtCognome, fmtNome } from "./shared";
import FaqPagina from "./FaqPagina";

// ── Costanti ──────────────────────────────────────────────────────────────────
const QUALIFICHE = ["VF", "VE", "VC", "CS", "CSE", "CR", "IA", "IAE", "IAC", "DCS", "VD", "DVD", "DIR", "DIRSUP"];
const TURNI      = ["A", "B", "C", "D", "AC", "BD", "GG"];
const SALTI      = [1, 2, 3, 4, 5, 6, 7, 8];

// Palette colori avatar — scelta deterministica da cognome
const AVATAR_COLORS = [
  ["#7c3aed", "#4c1d95"], // viola
  ["#2563eb", "#1e3a8a"], // blu
  ["#0891b2", "#164e63"], // ciano
  ["#059669", "#064e3b"], // verde
  ["#d97706", "#78350f"], // ambra
  ["#dc2626", "#7f1d1d"], // rosso
  ["#db2777", "#831843"], // rosa
  ["#65a30d", "#365314"], // lime
];

const avatarColor = (cognome = "") => {
  let h = 0;
  for (let i = 0; i < cognome.length; i++) h = (h * 31 + cognome.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// ── Avatar iniziali ───────────────────────────────────────────────────────────
const InitialsAvatar = ({ cognome, nome, size = 96 }) => {
  const initials =
    `${(cognome?.[0] || "").toUpperCase()}${(nome?.[0] || "").toUpperCase()}` || "?";
  const [bg, dark] = avatarColor(cognome);
  return (
    <div
      style={{
        width: size, height: size,
        borderRadius: "50%",
        background: `linear-gradient(135deg, ${bg}, ${dark})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.35,
        fontWeight: 700,
        color: "#fff",
        letterSpacing: "0.05em",
        flexShrink: 0,
        border: "2px solid rgba(255,255,255,0.15)",
        userSelect: "none",
      }}
    >
      {initials}
    </div>
  );
};

// ── Componenti UI locali ──────────────────────────────────────────────────────
const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-medium text-slate-400 mb-1">
    {children}{required && <span className="text-orange-400 ml-0.5">*</span>}
  </label>
);

const SelectField = ({ label, value, onChange, options, placeholder, disabled, required }) => {
  if (disabled) return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="text-white text-sm py-1 min-h-[1.5rem]">{value || <span className="text-slate-500">—</span>}</div>
    </div>
  );
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <select
        className="form-input"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">{placeholder || "— seleziona —"}</option>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder, type = "text", required, icon, showError, disabled }) => {
  if (disabled) return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="text-white text-sm py-1 min-h-[1.5rem]">{value || <span className="text-slate-500">—</span>}</div>
    </div>
  );
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="relative">
        {icon && (
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            {icon}
          </span>
        )}
        <input
          className="form-input"
          style={icon ? { paddingLeft: "2rem" } : undefined}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {showError && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 text-sm pointer-events-none" title="Numero non valido (meno di 10 cifre)">✕</span>
        )}
      </div>
    </div>
  );
};

// ── ProfiloPage ───────────────────────────────────────────────────────────────
const ProfiloPage = ({ onNavigate, api, operatore, onTokenChange }) => {
  const [form, setForm]       = useState({
    qualifica: "", cognome: operatore?.cognome || "", nome: operatore?.nome || "",
    regione: "", comando: "", sede: "", turno: "", salto: "", telefono: "",
  });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [dirty, setDirty]       = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isSedeVvf, setIsSedeVvf] = useState(false);
  const savedFormRef            = useRef(null);

  // ── Cambio password ──
  const [pw, setPw]             = useState({ vecchia: "", nuova: "", conferma: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwOpen, setPwOpen]     = useState(false);
  const pwChecks = {
    len:    pw.nuova.length >= 8,
    num:    /\d/.test(pw.nuova),
    maiusc: /[A-Z]/.test(pw.nuova),
    spec:   /[^A-Za-z0-9]/.test(pw.nuova),
  };
  const pwValida = pwChecks.len && pwChecks.num && pwChecks.maiusc && pwChecks.spec;

  const comandi = getComandi(form.regione);
  const sedi    = getSedi(form.regione, form.comando);
  // Sede bloccata per ruoli elevated: solo l'admin può modificarla tramite Sedi VVF
  const sedeLockata = ["rop", "ror", "ron", "dir_reg", "con", "com_prov"].includes(operatore?.ruolo);
  // Editabilità per ruolo in base all'ambito di visibilità:
  // - RON (nazionale): può cambiare regione/comando/sede (vede già tutto)
  // - ROR (regionale): solo comando/sede; la regione = il suo limite di permesso
  // - ROP (provinciale): solo sede; regione+comando definiscono il suo ambito
  const _ruolo = operatore?.ruolo;
  const regioneEditabile = _ruolo === "ron";
  const comandoEditabile = _ruolo === "ron" || _ruolo === "ror";
  const sedeEditabile    = _ruolo === "ron" || _ruolo === "ror" || _ruolo === "rop";
  // True solo quando tutti e tre i campi sono liberi (RON) → niente banner di lock.
  const tuttoEditabile   = regioneEditabile && comandoEditabile && sedeEditabile;
  // Per il ruolo operatore Regione/Comando/Sede sono obbligatori
  const campiSediObbligatori = operatore?.ruolo === "operatore";

  // ── Carica profilo ─────────────────────────────────────────────────────────
  const loadProfilo = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getProfilo();
      const loaded = {
        qualifica: data.qualifica || "",
        cognome:   data.cognome   || "",
        nome:      data.nome      || "",
        regione:   data.regione   || "",
        comando:   data.comando   || "",
        sede:      data.sede      || "",
        turno:     data.turno     || "",
        salto:     data.salto != null ? String(data.salto) : "",
        telefono:  data.telefono  || "",
      };
      savedFormRef.current = loaded;
      setForm(loaded);
      setIsSedeVvf(!!data.is_sede_vvf);
      setDirty(false);
    } catch {
      toast.error("Errore caricamento profilo");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadProfilo(); }, [loadProfilo]);

  // ── Cambio campo con reset a cascata ───────────────────────────────────────
  const set = (field) => (value) => {
    setForm(p => {
      const next = { ...p, [field]: value };
      if (field === "regione") { next.comando = ""; next.sede = ""; }
      if (field === "comando") { next.sede = ""; }
      return next;
    });
    setDirty(true);
  };

  // ── Annulla modifiche ──────────────────────────────────────────────────────
  const handleCancel = () => {
    if (savedFormRef.current) setForm({ ...savedFormRef.current });
    setEditMode(false);
    setDirty(false);
  };

  // ── Salva ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.cognome.trim() || !form.nome.trim()) {
      toast.error("Cognome e Nome sono obbligatori");
      return;
    }
    if (campiSediObbligatori && (!form.regione || !form.comando || !form.sede)) {
      toast.error("Regione, Comando e Sede sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const { data: saved } = await api.updateProfilo({
        qualifica: form.qualifica,
        cognome:   fmtCognome(form.cognome.trim()),
        nome:      fmtNome(form.nome.trim()),
        regione:   form.regione,
        comando:   form.comando,
        sede:      form.sede,
        turno:     form.turno,
        salto:     form.salto !== "" ? Number(form.salto) : null,
        telefono:  form.telefono,
      });
      toast.success("Profilo salvato");
      savedFormRef.current = { ...form };
      if (saved?.is_sede_vvf !== undefined) setIsSedeVvf(!!saved.is_sede_vvf);
      setDirty(false);
      setEditMode(false);
    } catch (e) {
      toast.error(apiErr(e, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  // ── Cambia password ──────────────────────────────────────────────────────────
  const handleCambiaPassword = async () => {
    if (!pw.vecchia) { toast.error("Inserisci la password attuale"); return; }
    if (!pwValida) { toast.error("La nuova password non rispetta i requisiti di sicurezza"); return; }
    if (pw.nuova !== pw.conferma) { toast.error("Le due nuove password non coincidono"); return; }
    setPwSaving(true);
    try {
      const { data } = await api.cambiaPassword({ vecchia_password: pw.vecchia, nuova_password: pw.nuova });
      // Il backend invalida le altre sessioni e restituisce un token nuovo per questa
      if (data?.access_token) {
        localStorage.setItem("tas_token", data.access_token);
        onTokenChange?.(data.access_token);
      }
      toast.success("Password aggiornata con successo");
      setPw({ vecchia: "", nuova: "", conferma: "" });
      setPwOpen(false);
    } catch (e) {
      toast.error(apiErr(e, "Errore cambio password"));
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        <RefreshCw size={24} className="animate-spin mr-2" /> Caricamento profilo…
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Il mio Profilo</h1>
          <p className="text-sm text-slate-400">{operatore?.email}</p>
        </div>
        <FaqPagina pagina="profilo" onNavigate={onNavigate} />
        {!editMode ? (
          <button className="btn btn-secondary flex items-center gap-2" onClick={() => setEditMode(true)}>
            <Edit2 size={15} /> Modifica
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary flex items-center gap-2" onClick={handleCancel}>
              <X size={15} /> Annulla
            </button>
            <button className="btn btn-primary flex items-center gap-2" onClick={handleSave} disabled={saving || !dirty}>
              <Save size={15} /> {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        )}
      </div>


      {/* ── Card identità ──────────────────────────────────────────────── */}
      <div className="tactical-card p-5">
        <div className="flex items-center gap-5">
          <InitialsAvatar cognome={form.cognome} nome={form.nome} size={80} />
          <div>
            <div className="text-white font-semibold text-lg leading-tight">
              {form.cognome || "–"} {form.nome}
            </div>
            {form.qualifica && (
              <span className="inline-block mt-1 px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/40 text-xs font-medium">
                {form.qualifica}
              </span>
            )}
            <div className="text-xs text-slate-400 mt-1.5">
              {[form.sede, form.comando, form.regione].filter(Boolean).join(" · ") || "Sede non impostata"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{operatore?.email}</div>
          </div>
        </div>
      </div>

      {/* ── Dati anagrafici ─────────────────────────────────────────────── */}
      <div className="tactical-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <User size={16} className="text-orange-400" />
          <h2 className="text-sm font-semibold text-white uppercase tracking-wide">Dati Anagrafici</h2>
        </div>
        <div className={`grid grid-cols-1 ${isSedeVvf ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-4`}>
          {!isSedeVvf && (
            <SelectField
              label="Qualifica"
              value={form.qualifica}
              onChange={set("qualifica")}
              options={QUALIFICHE}
              placeholder="— nessuna —"
			  required
              disabled={!editMode}
            />
          )}
          <TextField
            label="Cognome"
            value={form.cognome}
            onChange={(v) => set("cognome")(fmtCognome(v))}
            placeholder="ROSSI"
            required
            disabled={!editMode}
          />
          <TextField
            label="Nome"
            value={form.nome}
            onChange={(v) => set("nome")(fmtNome(v))}
            placeholder="Mario"
            required
            disabled={!editMode}
          />
        </div>
      </div>

      {/* ── Sede ────────────────────────────────────────────────────────── */}
      <div className="tactical-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={16} className="text-orange-400" />
          <h2 className="text-sm font-semibold text-white uppercase tracking-wide">
            {isSedeVvf ? "Sede" : "Sede di Servizio"}
          </h2>
        </div>
        {sedeLockata && !tuttoEditabile && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700/50 text-xs text-blue-300 mb-4">
            <Lock size={12} className="text-blue-400 shrink-0" />
            {comandoEditabile
              ? "La regione definisce il tuo ambito regionale e non è modificabile — puoi modificare comando e sede"
              : sedeEditabile
                ? "Regione e comando definiscono il tuo ambito e non sono modificabili — puoi modificare solo la sede di servizio"
                : isSedeVvf
                  ? "Regione e comando impostati dall'amministratore (Sedi VVF) — non modificabili dal profilo"
                  : "Sede di servizio impostata dall'amministratore — non modificabile dal profilo"}
          </div>
        )}
        <div className={`grid grid-cols-1 ${(isSedeVvf && !sedeEditabile) ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-4`}>
          <SelectField
            label="Regione"
            value={form.regione}
            onChange={set("regione")}
            options={VVF_REGIONI}
            disabled={(sedeLockata && !regioneEditabile) || !editMode}
            required={campiSediObbligatori}
          />
          <SelectField
            label="Comando"
            value={form.comando}
            onChange={set("comando")}
            options={comandi}
            disabled={(sedeLockata && !comandoEditabile) || !editMode || !form.regione}
            placeholder={form.regione ? "— seleziona —" : "Prima seleziona regione"}
            required={campiSediObbligatori}
          />
          {(!isSedeVvf || sedeEditabile) && (
            <SelectField
              label="Sede"
              value={form.sede}
              onChange={set("sede")}
              options={sedi}
              disabled={(sedeLockata && !sedeEditabile) || !editMode || !form.comando}
              placeholder={form.comando ? "— seleziona —" : "Prima seleziona comando"}
              required={campiSediObbligatori}
            />
          )}
        </div>
      </div>

      {/* ── Contatti / Turno ─────────────────────────────────────────────── */}
      <div className="tactical-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16} className="text-orange-400" />
          <h2 className="text-sm font-semibold text-white uppercase tracking-wide">
            {isSedeVvf ? "Contatti" : "Turno e Contatti"}
          </h2>
        </div>
        <div className={`grid grid-cols-1 ${isSedeVvf ? "sm:grid-cols-1" : "sm:grid-cols-3"} gap-4`}>
          {!isSedeVvf && (
            <>
              <SelectField
                label="Turno"
                value={form.turno}
                onChange={set("turno")}
                options={TURNI}
                placeholder="— nessuno —"
				required
                disabled={!editMode}
              />
              <SelectField
                label="Salto"
                value={form.salto}
                onChange={set("salto")}
                options={SALTI.map(String)}
                placeholder="— nessuno —"
                disabled={!editMode}
              />
            </>
          )}
          <TextField
            label="Telefono"
            value={form.telefono}
            onChange={set("telefono")}
            placeholder="3xx…"
            type="tel"
            icon={<Phone size={14} />}
            showError={!!form.telefono && form.telefono.replace(/\s/g, "").length < 10}
			required
            disabled={!editMode}
          />
        </div>
      </div>

      {/* ── Sicurezza / Cambio password ──────────────────────────────────── */}
      <div className="tactical-card p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock size={16} className="text-orange-400" />
            <h2 className="text-sm font-semibold text-white uppercase tracking-wide">Sicurezza</h2>
          </div>
          {!pwOpen && (
            <button className="btn btn-secondary btn-sm" onClick={() => setPwOpen(true)}>Cambia password</button>
          )}
        </div>
        {!pwOpen ? (
          <p className="text-xs text-slate-400 mt-2">Aggiorna la tua password di accesso.</p>
        ) : (
          <div className="space-y-3 mt-4">
            <div>
              <FieldLabel required>Password attuale</FieldLabel>
              <input type="password" className="form-input" autoComplete="current-password"
                value={pw.vecchia} onChange={e => setPw(p => ({ ...p, vecchia: e.target.value }))} />
            </div>
            <div>
              <FieldLabel required>Nuova password</FieldLabel>
              <input type="password" className="form-input" autoComplete="new-password"
                value={pw.nuova} onChange={e => setPw(p => ({ ...p, nuova: e.target.value }))} />
              <div className="mt-1.5 space-y-1">
                {[
                  ["len", "Almeno 8 caratteri"],
                  ["num", "Almeno un numero"],
                  ["maiusc", "Almeno una lettera maiuscola"],
                  ["spec", "Almeno un carattere speciale"],
                ].map(([k, label]) => (
                  <div key={k} className={`flex items-center gap-1.5 text-xs ${pwChecks[k] ? "text-green-400" : "text-slate-500"}`}>
                    <span>{pwChecks[k] ? "✓" : "○"}</span><span>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <FieldLabel required>Conferma nuova password</FieldLabel>
              <input type="password" className="form-input" autoComplete="new-password"
                value={pw.conferma} onChange={e => setPw(p => ({ ...p, conferma: e.target.value }))} />
              {pw.conferma && pw.nuova !== pw.conferma && (
                <p className="text-red-400 text-xs mt-1">Le password non coincidono</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn btn-secondary" onClick={() => { setPwOpen(false); setPw({ vecchia: "", nuova: "", conferma: "" }); }}>Annulla</button>
              <button className="btn btn-primary flex items-center gap-2" onClick={handleCambiaPassword}
                disabled={pwSaving || !pw.vecchia || !pwValida || pw.nuova !== pw.conferma}>
                <Lock size={14} /> {pwSaving ? "Aggiornamento…" : "Aggiorna password"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Info sincronizzazione ────────────────────────────────────────── */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
        <svg className="flex-shrink-0 mt-0.5 w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
        <span>
          {isSedeVvf
            ? "I dati del profilo vengono sincronizzati automaticamente con l'elenco Sedi VVF. Regione e comando sono impostati dall'amministratore e determinano l'ambito di visibilità delle operazioni."
            : "I dati del profilo vengono sincronizzati automaticamente con la whitelist TAS2 se la tua email è presente nell'elenco del personale autorizzato."}
        </span>
      </div>

      {/* ── Pulsante salva sticky ─────────────────────────────────────────── */}
      {editMode && dirty && (
        <div className="sticky bottom-4 flex justify-end pointer-events-none">
          <button
            className="btn btn-primary shadow-2xl flex items-center gap-2 px-6 pointer-events-auto"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={15} />
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfiloPage;
